/** @format */

import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { forwardRef, useState } from 'react';

const KIND_LABELS = {
  brand: 'Marque',
  typology: 'Typologie',
  product: 'Produit',
};

// Live search with keyboard shortcuts list (ARIA combobox).
const CatalogSearch = forwardRef(
  ({ value, onChange, pending, suggestions, onPick, fullWidth }, inputRef) => {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const isOpen = open && suggestions.length > 0;
    const activeId =
      isOpen && active >= 0
        ? `catalog-suggestion-${suggestions[active]?.id}`
        : undefined;

    const pick = (suggestion) => {
      onPick(suggestion);
      setOpen(false);
      setActive(-1);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!isOpen) {
          setOpen(true);
          setActive(0);
        } else {
          setActive((i) => (i + 1) % suggestions.length);
        }
      } else if (e.key === 'ArrowUp' && isOpen) {
        e.preventDefault();
        setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (isOpen && active >= 0) pick(suggestions[active]);
        else setOpen(false);
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        setOpen(false);
        setActive(-1);
      }
    };

    return (
      <Box
        sx={{
          position: 'relative',
          width: fullWidth ? '100%' : 360,
          maxWidth: '100%',
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="Rechercher un produit, une marque, une référence…  ( / )"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
          inputProps={{
            role: 'combobox',
            'aria-label': 'Rechercher dans le catalogue',
            'aria-expanded': isOpen,
            'aria-controls': 'catalog-suggestions',
            'aria-autocomplete': 'list',
            'aria-activedescendant': activeId,
          }}
          InputProps={{
            sx: { minHeight: 44, borderRadius: 3, bgcolor: 'background.paper' },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {pending && (
                  <CircularProgress
                    size={18}
                    aria-label="Recherche en cours"
                    sx={{ mr: 1 }}
                  />
                )}
                {value && (
                  <IconButton
                    aria-label="Effacer la recherche"
                    onClick={() => onChange('')}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    sx={{ width: 44, height: 44 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
        {isOpen && (
          <Paper
            id="catalog-suggestions"
            role="listbox"
            elevation={6}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 10,
              py: 0.5,
              borderRadius: 2,
            }}
          >
            {suggestions.map((s, index) => (
              <Box
                key={s.id}
                id={`catalog-suggestion-${s.id}`}
                role="option"
                aria-selected={index === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setActive(index)}
                sx={{
                  minHeight: 44,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  bgcolor: index === active ? 'action.selected' : 'transparent',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'text.secondary',
                    width: 64,
                    flexShrink: 0,
                  }}
                >
                  {KIND_LABELS[s.kind]}
                </Typography>
                <Typography noWrap sx={{ fontSize: 15 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    );
  },
);

CatalogSearch.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  pending: PropTypes.bool.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string }))
    .isRequired,
  onPick: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
};

CatalogSearch.defaultProps = { fullWidth: false };

export default CatalogSearch;
